import { cn } from "@plasma/utils/cn";
import { Check, ChevronsUpDown, PlusCircle } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback } from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";

import type { SearchOptions } from "./locations-types";

const SORT_BY_VALUES = [
  "custom",
  "name-asc",
  "name-desc",
  "createdAt",
] as const;
const isSortByOption = enumIncludes(SORT_BY_VALUES);

interface LocationsListFilterProps {
  filterQuery: string;
  setFilterQuery: Dispatch<SetStateAction<string>>;

  filterSortBy: SearchOptions["sortBy"];
  setFilterSortBy: Dispatch<SetStateAction<SearchOptions["sortBy"]>>;

  filterStyle: SearchOptions["filters"]["style"];
  setFilterStyle: Dispatch<SetStateAction<SearchOptions["filters"]["style"]>>;

  filterOperationalStatus: SearchOptions["filters"]["operationalStatus"];
  setFilterOperationalStatus: Dispatch<
    SetStateAction<SearchOptions["filters"]["operationalStatus"]>
  >;
}

const SORT_BY_OPTIONS = [
  { value: "custom", label: "Custom" },
  { value: "createdAt", label: "Creation Date" },
  { value: "name-asc", label: "Location name (A-Z)" },
  { value: "name-desc", label: "Location name (Z-A)" },
] as const;

const STYLE_OPTIONS: { label: string; value: "QSR" | "FSR" }[] = [
  { label: "QSR", value: "QSR" },
  { label: "FSR", value: "FSR" },
];

const OPERATIONAL_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "Open", value: "OPEN" },
  { label: "Temporarily Closed", value: "TEMPORARILY_CLOSED" },
  { label: "Permanently Closed", value: "PERMANENTLY_CLOSED" },
];

export function LocationsListFilter({
  filterQuery,
  setFilterQuery,
  filterSortBy,
  setFilterSortBy,
  filterStyle,
  setFilterStyle,
  filterOperationalStatus,
  setFilterOperationalStatus,
}: LocationsListFilterProps) {
  const handleSortByChange = useCallback(
    (value: string) => {
      setFilterSortBy(parseEnumValue(value, isSortByOption, "custom"));
    },
    [setFilterSortBy]
  );

  const handleStyleChange = useCallback(
    (values: typeof filterStyle) => {
      setFilterStyle(values);
    },
    [setFilterStyle]
  );

  const handleOperationalStatusChange = useCallback(
    (values: typeof filterOperationalStatus) => {
      setFilterOperationalStatus(values);
    },
    [setFilterOperationalStatus]
  );

  return (
    <div className="flex flex-wrap items-center gap-3" id="list-filter">
      <Input
        className="w-56 shrink-0"
        onChange={(e) => setFilterQuery(e.target.value)}
        placeholder="Search name..."
        value={filterQuery}
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button
            className="shrink-0 whitespace-nowrap"
            size="sm"
            type="button"
            variant="outline"
          >
            <span className="truncate">
              {SORT_BY_OPTIONS.find((o) => o.value === filterSortBy)?.label ??
                "Sort by"}
            </span>
            <ChevronsUpDown className="ml-1 size-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-52 p-0">
          <Command>
            <CommandList className="max-h-full">
              <CommandGroup>
                {SORT_BY_OPTIONS.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSortByChange(option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        filterSortBy === option.value
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FilterSelect
        onSelectedValuesChange={handleStyleChange}
        options={STYLE_OPTIONS}
        placeholder="Search Style..."
        selectedValues={filterStyle}
        title="Style"
      />
      <FilterSelect
        onSelectedValuesChange={handleOperationalStatusChange}
        options={OPERATIONAL_STATUS_OPTIONS}
        placeholder="Search Status..."
        selectedValues={filterOperationalStatus}
        title="Operational Status"
      />
    </div>
  );
}
const FilterSelect = <T extends string>({
  title,
  options,
  placeholder,
  selectedValues,
  onSelectedValuesChange,
}: {
  title?: string;
  options: {
    label: string;
    value: T;
  }[];
  placeholder: string;
  selectedValues: T[];
  onSelectedValuesChange: (selectedValues: T[]) => void;
}) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="shrink-0 whitespace-nowrap"
          size="sm"
          type="button"
          variant="outline"
        >
          <PlusCircle className="size-4 shrink-0" />
          <span className="truncate">{title}</span>
          {selectedValues.length > 0 && (
            <>
              <Badge
                className="shrink-0 rounded-md px-1 font-normal lg:hidden"
                variant="secondary"
              >
                {selectedValues.length}
              </Badge>
              <div className="hidden shrink-0 space-x-1 lg:flex">
                {selectedValues.length > 2 ? (
                  <Badge className="px-1 font-normal" variant="secondary">
                    {selectedValues.length} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.includes(option.value))
                    .map((option) => (
                      <Badge
                        className="rounded-md px-1 font-normal"
                        key={option.value}
                        variant="secondary"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList className="max-h-full">
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <CommandItem
                    className="flex items-center gap-2"
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        onSelectedValuesChange(
                          selectedValues.filter((v) => v !== option.value)
                        );
                      } else {
                        onSelectedValuesChange([
                          ...selectedValues,
                          option.value,
                        ]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="pointer-events-none"
                    />
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className="justify-center text-center"
                    onSelect={() => onSelectedValuesChange([])}
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
